import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_BADGES = {
  en_attente: 'bg-blue-600/20 text-blue-300 border-blue-500/30',
  verifie: 'bg-emerald-600/20 text-emerald-300 border-emerald-500/30',
  refuse: 'bg-red-600/20 text-red-300 border-red-500/30'
};

const DossierCard = ({ dossier, onDecision, deciding }) => {
  const viewDocument = async (docId) => {
    try {
      const response = await axios.get(`${API}/admin/verifications/documents/${docId}/fichier`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      window.open(url, '_blank');
    } catch (error) {
      alert('Erreur lors de l\'ouverture du document');
    }
  };

  const handleRefuse = () => {
    const motif = window.prompt('Motif du refus (sera communiqué à l\'utilisateur) :');
    if (motif === null) return;
    onDecision(dossier.user_id, 'refuse', motif);
  };

  const isPending = dossier.verification_status === 'en_attente';

  return (
    <div data-testid={`dossier-${dossier.user_id}`} className="bg-gradient-to-br from-slate-800/50 to-slate-700/50 border border-slate-600/50 rounded-xl p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h4 className="text-white font-semibold text-lg">{dossier.email}</h4>
          <p className="text-slate-400 text-sm">
            {dossier.user_type === 'magasin' ? '🏢 Magasin d\'optique' : '👓 Opticien'}
            {dossier.created_at && <span> — inscrit le {new Date(dossier.created_at).toLocaleDateString('fr-FR')}</span>}
          </p>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${STATUS_BADGES[dossier.verification_status] || STATUS_BADGES.en_attente}`}>
          {dossier.verification_status === 'verifie' ? 'Vérifié' : dossier.verification_status === 'refuse' ? 'Refusé' : 'En attente'}
        </span>
      </div>

      <div className="space-y-2 mb-4">
        {dossier.documents.map((doc) => (
          <div key={doc.id} className="flex items-center justify-between bg-slate-700/40 rounded-lg px-4 py-3">
            <div className="flex items-center space-x-3">
              <span>📄</span>
              <div>
                <p className="text-white text-sm font-medium">{doc.label}</p>
                <p className="text-slate-400 text-xs">{doc.filename} — {Math.round((doc.taille || 0) / 1024)} Ko</p>
              </div>
            </div>
            <button
              data-testid={`view-doc-${doc.id}`}
              onClick={() => viewDocument(doc.id)}
              className="text-blue-400 hover:text-blue-300 text-sm font-medium px-3 py-1 rounded-lg hover:bg-blue-600/10 transition-colors"
            >
              👁️ Voir
            </button>
          </div>
        ))}
      </div>

      {dossier.verification_status === 'refuse' && dossier.motif_refus && (
        <p className="text-red-300 text-sm mb-4">Motif du refus : {dossier.motif_refus}</p>
      )}

      {isPending && (
        <div className="flex space-x-3">
          <button
            data-testid={`approve-btn-${dossier.user_id}`}
            onClick={() => onDecision(dossier.user_id, 'approuve')}
            disabled={deciding}
            className="flex-1 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 disabled:opacity-50 text-white font-medium py-3 rounded-lg transition-all duration-300"
          >
            ✅ Approuver
          </button>
          <button
            data-testid={`refuse-btn-${dossier.user_id}`}
            onClick={handleRefuse}
            disabled={deciding}
            className="flex-1 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 disabled:opacity-50 text-white font-medium py-3 rounded-lg transition-all duration-300"
          >
            ❌ Refuser
          </button>
        </div>
      )}
    </div>
  );
};

export const AdminVerifications = () => {
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('en_attente');
  const [deciding, setDeciding] = useState(false);

  const fetchDossiers = async (statusFilter) => {
    setLoading(true);
    try {
      const params = statusFilter && statusFilter !== 'tous' ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API}/admin/verifications/dossiers${params}`);
      setDossiers(response.data.dossiers);
    } catch (error) {
      console.error('Erreur chargement dossiers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDossiers(filter); }, [filter]);

  const handleDecision = async (userId, decision, motif = null) => {
    setDeciding(true);
    try {
      await axios.put(`${API}/admin/verifications/${userId}/decision`, { decision, motif });
      await fetchDossiers(filter);
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la décision');
    } finally {
      setDeciding(false);
    }
  };

  const filters = [
    { id: 'en_attente', label: '⏳ En attente' },
    { id: 'verifie', label: '✅ Vérifiés' },
    { id: 'refuse', label: '❌ Refusés' },
    { id: 'tous', label: '📋 Tous' }
  ];

  return (
    <div className="space-y-6" data-testid="admin-verifications">
      <div>
        <h3 className="text-2xl font-bold text-white mb-2 flex items-center">
          <span className="mr-3">🛡️</span>
          Dossiers de vérification
        </h3>
        <p className="text-slate-400">Examinez les documents soumis et approuvez ou refusez les comptes.</p>
      </div>

      <div className="flex space-x-2">
        {filters.map((f) => (
          <button
            key={f.id}
            data-testid={`filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`px-4 py-2 rounded-lg text-sm transition-all duration-200 ${
              filter === f.id ? 'bg-blue-600/30 text-blue-300 border border-blue-500/30' : 'text-slate-400 hover:text-white hover:bg-white/10'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : dossiers.length === 0 ? (
        <div className="bg-slate-700/30 rounded-xl p-12 text-center border border-slate-600/30">
          <div className="text-6xl mb-4">📭</div>
          <h3 className="text-xl font-semibold text-white mb-2">Aucun dossier</h3>
          <p className="text-slate-400">Aucun dossier de vérification pour ce filtre.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {dossiers.map((dossier) => (
            <DossierCard key={dossier.user_id} dossier={dossier} onDecision={handleDecision} deciding={deciding} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminVerifications;
